// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CartLineContext } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { CartLineCardCondensedStyles } from "@insite/content-library/Components/CartLineCardCondensed";
import { CartLinesListHeaderStyles } from "@insite/content-library/Components/CartLinesListHeader";
import { PaginationPresentationProps } from "@insite/mobius/Pagination";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LinesHeader, { LinesHeaderStyles } from "@steiner/App/Components/Cart/LinesHeader";
import TableCounter from "@steiner/App/Components/Common/TableCounter";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import CartLineCardExpanded, { CartLineCardExpandedStyles } from "@steiner/Overrides/Widgets/Cart/CartLineCardExpanded";
import React, { FC } from "react";
import { connect, ResolveThunks, useSelector } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    isCondensed: boolean;
    hideCondensedSelector?: boolean;
    onChangeIsCondensed?: (event: React.SyntheticEvent<Element, Event>, value: boolean) => void;
    editable: boolean;
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    extendedStyles?: CartLinesListStyles;
    tableCounterTitle?: string;
    // XNG-Changes
    editableNote?: boolean;
    showAddToList?: boolean;
    showLineNotes?: boolean;
}

const mapDispatchToProps = {
    updateCartLine,
    removeCartLine,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface CartLinesListStyles {
    wrapper?: InjectableCss;
    header?: CartLinesListHeaderStyles;
    cardCondensed?: CartLineCardCondensedStyles;
    cardExpanded?: CartLineCardExpandedStyles;
    pagination?: PaginationPresentationProps;
    // XNG-Changes
    linesHeaderStyles?: LinesHeaderStyles;
}

export const orderLinesListStyles: CartLinesListStyles = {
    pagination: {
        cssOverrides: {
            pagination: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
};

const CartLinesList: FC<Props> = ({
    cart,
    promotions,
    isCondensed,
    hideCondensedSelector,
    onChangeIsCondensed,
    editable,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    // XNG-Changes
    tableCounterTitle,
    editableNote = true,
    showAddToList = true,
    showLineNotes = true,
    updateCartLine,
    removeCartLine,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));

    const { productSettings } = useSelector((state: CustomAppState) => getSettingsCollection(state));

    if (!cart.cartLines) {
        return null;
    }

    const cartLines =
        cart.status === "Cart" ? cart.cartLines.filter(cartline => !cartline.quoteRequired) : cart.cartLines;

    // XNG-Changes: LinesHeader
    return (
        <StyledWrapper {...styles.wrapper} data-test-selector={`cardLinesList_${cart.id}`}>
            <TableCounter
                watchResize={true}
                totalItemsCount={cart.cartLines.length}
                accordionOnSmallScreen={false}
                headingTitle={tableCounterTitle}
            >
                <LinesHeader extendedStyles={styles.linesHeaderStyles} />
                {cartLines!.map(cartLine => {
                    const cartLinePromotions = promotions?.filter(promotion => promotion.orderLineId === cartLine.id);
                    return (
                        <CartLineContext.Provider value={cartLine} key={cartLine.id}>
                            <CartLineCardExpanded
                                cart={cart}
                                promotions={cartLinePromotions}
                                editable={editable}
                                extendedStyles={isCondensed ? styles.cardCondensed : styles.cardExpanded}
                                // XNG-Changes (showSavingsAmount , showSavingsPercent ) applied from productSettings
                                // showSavingsAmount={showSavingsAmount}
                                // showSavingsPercent={showSavingsPercent}
                                // showInvalidPriceMessage={true}   // its default value true in CartLineCardExpanded
                                showInventoryAvailability={true}
                                productSettings={productSettings}
                                showLineNotes={showLineNotes}
                                showAddToList={showAddToList}
                                showRemoveAction={true}
                                editableNote={editableNote}
                                updateCartLine={updateCartLine}
                                removeCartLine={removeCartLine}
                            />
                        </CartLineContext.Provider>
                    );
                })}
            </TableCounter>
        </StyledWrapper>
    );
};

export default connect(null, mapDispatchToProps)(CartLinesList);
